// services/riskMessageService.js - FIXED VERSION
import { fetchApi } from "./api";

const riskMessageService = {
  // Create a new risk message with simplified schema
  createRiskMessage: async (
    content,
    messageType = "isUser",
    processWithAgent = true,
    metadata = {},
    conversationId,
    projectId = null
  ) => {
    try {
      // Get organization from localStorage to add domain parameter
      const organization = JSON.parse(localStorage.getItem("organization") || "{}");
      const domain = organization?.type || "general";

      const requestBody = {
        conversation_id: conversationId, // Use conversation_id from UI directly
        content,
        message_type: messageType,
        process_with_agent: processWithAgent, // UI flag only, not stored in DB
        domain, // Add domain parameter
        metadata, // Single metadata field contains all data
      };

      // Add project_id if provided
      if (projectId) {
        requestBody.project_id = projectId;
      }

      const response = await fetchApi("/api/risk-messages/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      return response;
    } catch (error) {
      console.error("Error creating risk message:", error);
      throw error;
    }
  },

  // FIXED: Create a new message with agent processing
  createMessage: async (conversationId, messageData, projectId = null) => {
    try {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }

      // FIXED: Extract content correctly from messageData
      let content, message_type, process_with_agent, metadata;

      if (typeof messageData === "string") {
        // If messageData is a string, treat it as content
        content = messageData;
        message_type = "isUser";
        process_with_agent = true;
        metadata = {};
      } else {
        // Extract from object - FIXED: use 'content' not 'agent_message'
        content = messageData.content || messageData.text || "";
        message_type = messageData.message_type || "isUser";
        process_with_agent = messageData.process_with_agent !== false;
        metadata = messageData.metadata || {};
      }

      const response = await riskMessageService.createRiskMessage(
        content,
        message_type,
        process_with_agent,
        metadata,
        conversationId,
        projectId // Pass project_id to backend
      );

      // Check if we have proper response structure
      if (!response?.user_message?.id) {
        throw new Error("Failed to create message: Invalid response format");
      }

      // Return complete message data with simplified structure
      return {
        ...response,
        user_message: {
          ...response.user_message,
          // Single metadata field contains everything
          metadata: response.user_message.metadata || {},
          // For backwards compatibility, extract common fields from metadata
          table_data: response.user_message.metadata?.table_data || {},
          summary_stats: response.user_message.metadata?.summary_stats || {},
          success: response.user_message.metadata?.success !== false,
          error: response.user_message.metadata?.error || null,
        },
        agent_message: response.agent_message
          ? {
              ...response.agent_message,
              // Single metadata field contains everything
              metadata: response.agent_message.metadata || {},
              // For backwards compatibility, extract common fields from metadata
              table_data: response.agent_message.metadata?.table_data || {},
              summary_stats: response.agent_message.metadata?.summary_stats || {},
              success: response.agent_message.metadata?.success !== false,
              error: response.agent_message.metadata?.error || null,
            }
          : null,
      };
    } catch (error) {
      console.error("Error in createMessage:", error);
      throw error;
    }
  },

  // Create a message with file uploads (new method for document upload)
  createMessageWithFiles: async (formData) => {
    try {
      // Get organization from localStorage to add domain parameter
      const organization = JSON.parse(localStorage.getItem("organization") || "{}");
      const domain = organization?.type || "general";

      // Add domain to FormData
      formData.append("domain", domain);

      const response = await fetchApi("/api/risk-messages/", {
        method: "POST",
        body: formData, // FormData automatically sets correct Content-Type
      });

      // Check if we have proper response structure
      if (!response?.user_message?.id) {
        throw new Error("Failed to upload and process documents: Invalid response format");
      }

      // Return complete message data with file processing results
      return {
        ...response,
        user_message: {
          ...response.user_message,
          metadata: response.user_message.metadata || {},
          table_data: response.user_message.metadata?.table_data || {},
          summary_stats: response.user_message.metadata?.summary_stats || {},
          success: response.user_message.metadata?.success !== false,
          error: response.user_message.metadata?.error || null,
        },
        agent_message: response.agent_message
          ? {
              ...response.agent_message,
              metadata: response.agent_message.metadata || {},
              table_data: response.agent_message.metadata?.table_data || {},
              summary_stats: response.agent_message.metadata?.summary_stats || {},
              success: response.agent_message.metadata?.success !== false,
              error: response.agent_message.metadata?.error || null,
            }
          : null,
      };
    } catch (error) {
      console.error("Error in createMessageWithFiles:", error);
      throw error;
    }
  },

  // Get messages for a conversation
  getMessages: async (conversationId, options = {}) => {
    try {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }

      const params = new URLSearchParams({
        conversation_id: conversationId,
        ...options,
      });

      const url = `/api/risk-messages/?${params.toString()}`;

      const response = await fetchApi(url);

      // Handle the response structure from backend
      if (response && response.messages && Array.isArray(response.messages)) {
        // Map messages to include backwards compatibility fields
        return response.messages.map((msg) => ({
          ...msg,
          content: msg.content,
          metadata: msg.metadata || {},
          table_data: msg.metadata?.table_data || {},
          summary_stats: msg.metadata?.summary_stats || {},
          analysis_metadata: msg.metadata || {},
          success: msg.metadata?.success !== false,
          error: msg.metadata?.error || null,
        }));
      } else if (Array.isArray(response)) {
        return response.map((msg) => ({
          ...msg,
          content: msg.content,
          metadata: msg.metadata || {},
          table_data: msg.metadata?.table_data || {},
          summary_stats: msg.metadata?.summary_stats || {},
          analysis_metadata: msg.metadata || {},
          success: msg.metadata?.success !== false,
          error: msg.metadata?.error || null,
        }));
      }

      return [];
    } catch (error) {
      console.error("Error fetching messages:", error);
      return [];
    }
  },

  // Clear entire conversation - Updated API endpoint
  clearConversation: async (conversationId) => {
    try {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }

      const response = await fetchApi(`/api/risk-messages/conversation/${conversationId}`, {
        method: "DELETE",
      });

      return response;
    } catch (error) {
      console.error(`Error clearing conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Update a message
  updateMessage: async (messageId, content, metadata = null) => {
    try {
      const body = { content };
      if (metadata !== null) {
        body.metadata = metadata;
      }

      const response = await fetchApi(`/api/risk-messages/${messageId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      // Return with backwards compatibility fields
      return {
        ...response,
        metadata: response.metadata || {},
        table_data: response.metadata?.table_data || {},
        summary_stats: response.metadata?.summary_stats || {},
        analysis_metadata: response.metadata || {},
      };
    } catch (error) {
      console.error(`Error updating message ${messageId}:`, error);
      throw error;
    }
  },

  // Delete a message
  deleteMessage: async (messageId) => {
    try {
      const response = await fetchApi(`/api/risk-messages/${messageId}`, {
        method: "DELETE",
      });
      return response;
    } catch (error) {
      console.error(`Error deleting message ${messageId}:`, error);
      throw error;
    }
  },

  // Quick analysis using the unified agent
  analyzeRiskWithAgent: async (conversationId, analysisType = "detailed") => {
    try {
      // Create a specific analysis prompt
      const analysisPrompts = {
        detailed:
          "Provide a detailed analysis of this risk, including root causes, potential impacts, and mitigation strategies.",
        similar: "Find and compare similar risks in our system. Show patterns and common factors.",
        action_plan:
          "Create a specific action plan to address this risk, with clear steps and timelines.",
        compliance:
          "Analyze compliance implications of this risk against relevant standards and regulations.",
      };

      const prompt = analysisPrompts[analysisType] || analysisPrompts.detailed;

      // Use the message creation endpoint for analysis
      const response = await riskMessageService.createMessage(conversationId, {
        content: prompt,
        message_type: "isUser",
        metadata: { analysis_type: analysisType },
      });

      return {
        userMessage: response.user_message,
        agentMessage: response.agent_message,
        analysisData: response.metadata,
        contextUsed: response.metadata?.context_used,
      };
    } catch (error) {
      console.error("Error in quick analysis:", error);
      throw error;
    }
  },

  // Custom analysis with specific prompt
  customMCPAnalysis: async (conversationId, customPrompt, analysisType = "custom") => {
    try {
      const response = await riskMessageService.createMessage(conversationId, {
        content: customPrompt,
        message_type: "isUser",
        metadata: { analysis_type: analysisType },
      });

      if (!response.success && !response.user_message) {
        throw new Error(response.error || "Custom analysis failed");
      }

      return response;
    } catch (error) {
      console.error("Error in custom analysis:", error);
      throw error;
    }
  },

  // Get full conversation history
  getConversationHistory: async (conversationId) => {
    try {
      const messages = await riskMessageService.getMessages(conversationId, { limit: 1000 });
      return messages || [];
    } catch (error) {
      console.error("Error getting conversation history:", error);
      throw error;
    }
  },

  // Get risk statistics
  getRiskStatistics: async (orgId) => {
    try {
      const response = await fetchApi(`/api/risk-error/summary?org_id=${orgId}`);
      return response;
    } catch (error) {
      console.error("Error getting risk statistics:", error);
      throw error;
    }
  },

  // Search messages
  searchMessages: async (query, conversationId = null) => {
    try {
      const params = new URLSearchParams();
      params.append("query", query);
      if (conversationId) {
        params.append("conversation_id", conversationId);
      }

      const response = await fetchApi(`/api/risk-messages/search?${params.toString()}`);

      // Add backwards compatibility fields
      if (response && response.messages) {
        return {
          ...response,
          messages: response.messages.map((msg) => ({
            ...msg,
            metadata: msg.metadata || {},
            table_data: msg.metadata?.table_data || {},
            summary_stats: msg.metadata?.summary_stats || {},
            analysis_metadata: msg.metadata || {},
          })),
        };
      }

      return response;
    } catch (error) {
      console.error("Error searching messages:", error);
      throw error;
    }
  },

  // Get message count for a conversation
  getMessageCount: async (conversationId) => {
    try {
      const response = await fetchApi(`/api/risk-messages/count?conversation_id=${conversationId}`);
      return response.count || 0;
    } catch (error) {
      console.error("Error getting message count:", error);
      return 0;
    }
  },

  // Clean up duplicate messages in a conversation
  cleanupDuplicates: async (conversationId) => {
    try {
      const response = await fetchApi(`/api/risk-messages/cleanup/${conversationId}`, {
        method: "POST",
      });

      return response;
    } catch (error) {
      console.error(`Error cleaning up duplicates for conversation ${conversationId}:`, error);
      throw error;
    }
  },
};

export default riskMessageService;
